import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Icon } from "@/components/ui/icon";

interface Skill {
  id: string;
  category: string;
  subcategory?: string;
  skill_name: string;
}

interface SkillsSectionProps {
  skills: Skill[];
  competencies?: Array<{ id: string; competency: string }>;
}

const categoryIcons: Record<string, string> = {
  "Analytics": "analytics",
  "Automation & AI": "smart_toy",
  "Development": "code",
  "E-commerce": "shopping_cart",
  "IT Infrastructure": "dns",
  "Marketing Tools": "campaign",
  "SEO": "search",
};

export const SkillsSection = ({ skills, competencies }: SkillsSectionProps) => {
  if (skills.length === 0 && (!competencies || competencies.length === 0)) return null;

  // Flatten skills into cards by category + subcategory
  const skillCards: Array<{ 
    title: string; 
    icon: string; 
    skills: Skill[];
    category: string;
  }> = [];

  skills.forEach((skill) => {
    const category = skill.category;
    const subcategory = skill.subcategory || "";
    
    // Create combined title
    const cardTitle = subcategory 
      ? `${subcategory} ${category}` 
      : category;
    
    // Find or create card
    let card = skillCards.find(c => c.title === cardTitle);
    if (!card) {
      card = {
        title: cardTitle,
        icon: categoryIcons[category] || "folder",
        skills: [],
        category: category,
      };
      skillCards.push(card);
    }
    
    card.skills.push(skill);
  });

  return (
    <section id="skills" className="section-padding">
      <div className="max-w-6xl mx-auto container-padding">
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3 animate-fade-in">
          Skills
        </h2>
        <p className="text-muted-foreground mb-10 animate-fade-in animation-delay-100">
          A snapshot of the tools and technologies I work with regularly.
        </p>

        {competencies && competencies.length > 0 && (
          <div className="mb-12 animate-fade-in animation-delay-200">
            <h3 className="text-lg font-semibold text-foreground mb-4">Core Competencies</h3>
            <div className="flex flex-wrap gap-2">
              {competencies.map((comp, index) => (
                <Badge
                  key={comp.id}
                  variant="success"
                  className="px-4 py-2 text-sm rounded-full animate-fade-in"
                  style={{ animationDelay: `${(index + 3) * 50}ms` }}
                >
                  {comp.competency}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {skills.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {skillCards.map((card, index) => (
              <Card 
                key={card.title} 
                className="hover:shadow-md transition-all duration-200 animate-fade-in"
                style={{ animationDelay: `${(index + 1) * 100}ms` }}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-3 text-base">
                    <Icon 
                      name={card.icon} 
                      className="text-primary" 
                      size={22}
                    />
                    <span className="flex-1">{card.title}</span>
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {card.skills.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {card.skills.map((skill) => (
                      <Badge
                        key={skill.id}
                        variant="success"
                        className="px-3 py-1 text-xs rounded-full"
                      >
                        {skill.skill_name}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
